import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { khoiAPI } from '../services/api';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import ImportExcel from '../components/ImportExcel';
import { FiPlus, FiEdit2, FiTrash2, FiBook, FiUpload, FiDownload, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import * as XLSX from 'xlsx';

const KhoiPage = () => {
  const { t } = useTranslation();
  const [khois, setKhois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedKhoi, setSelectedKhoi] = useState(null);
  const [formData, setFormData] = useState({ maKhoi: '', tenKhoi: '' });

  useEffect(() => {
    fetchKhois();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchKhois = async () => {
    try {
      const response = await khoiAPI.getAll();
      if (response.data.success) {
        setKhois(response.data.data);
      }
    } catch (error) {
      toast.error(t('khoi.cannotLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedKhoi) {
        await khoiAPI.update(selectedKhoi.khoi_id, formData);
        toast.success(t('khoi.updateSuccess'));
      } else {
        await khoiAPI.create(formData);
        toast.success(t('khoi.addSuccess'));
      }
      setModalOpen(false);
      setSelectedKhoi(null);
      setFormData({ maKhoi: '', tenKhoi: '' });
      fetchKhois();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.errorOccurred'));
    }
  };

  const handleEdit = (khoi) => {
    setSelectedKhoi(khoi);
    setFormData({ maKhoi: khoi.ma_khoi, tenKhoi: khoi.ten_khoi });
    setModalOpen(true);
  };

  const handleDelete = (khoi) => {
    setSelectedKhoi(khoi);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await khoiAPI.delete(selectedKhoi.khoi_id);
      toast.success(t('khoi.deleteSuccess'));
      setConfirmOpen(false);
      setSelectedKhoi(null);
      fetchKhois();
    } catch (error) {
      toast.error(t('khoi.cannotDelete'));
    }
  };

  const handleReorder = async (orderedIds) => {
    try {
      await khoiAPI.reorder(orderedIds);
      fetchKhois();
    } catch (error) {
      toast.error(t('common.cannotReorder'));
    }
  };

  const moveUp = (index) => {
    if (index > 0) {
      const newKhois = [...khois];
      [newKhois[index - 1], newKhois[index]] = [newKhois[index], newKhois[index - 1]];
      setKhois(newKhois);
      handleReorder(newKhois.map(k => k.khoi_id));
    }
  };

  const moveDown = (index) => {
    if (index < khois.length - 1) {
      const newKhois = [...khois];
      [newKhois[index], newKhois[index + 1]] = [newKhois[index + 1], newKhois[index]];
      setKhois(newKhois);
      handleReorder(newKhois.map(k => k.khoi_id));
    }
  };

  const handleImport = async (file) => {
    const response = await khoiAPI.import(file);
    if (response.data.success) {
      fetchKhois();
    }
    return response;
  };

  const handleExportExcel = () => {
    const header = ['Mã khối', 'Tên khối', 'Mô tả'];
    const rows = khois.map(k => [k.ma_khoi, k.ten_khoi, k.mo_ta || '']);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Khối học');
    XLSX.writeFile(wb, 'khoi_hoc.xlsx');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="khoi-page">
      <div className="page-header">
        <div className="header-content">
          <h2><FiBook /> {t('khoi.pageTitle')}</h2>
          <p>{t('khoi.pageSubtitle')}</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={handleExportExcel}>
            <FiDownload /> Export Excel
          </button>
          <button className="btn btn-secondary" onClick={() => setImportOpen(true)}>
            <FiUpload /> {t('khoi.importBtn')}
          </button>
          <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
            <FiPlus /> {t('khoi.addBtn')}
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('khoi.colOrder')}</th>
              <th>{t('khoi.colCode')}</th>
              <th>{t('khoi.colName')}</th>
              <th>{t('khoi.colDesc')}</th>
              <th>{t('khoi.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {khois.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  <FiBook />
                  <h3>{t('khoi.empty')}</h3>
                  <p>{t('khoi.emptyHint')}</p>
                </td>
              </tr>
            ) : (
              khois.map((khoi, index) => (
                <tr key={khoi.khoi_id}>
                  <td>
                    <div className="order-controls">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => moveUp(index)}
                        disabled={index === 0}
                        title={t('common.moveUp')}
                      >
                        <FiChevronUp />
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => moveDown(index)}
                        disabled={index === khois.length - 1}
                        title={t('common.moveDown')}
                      >
                        <FiChevronDown />
                      </button>
                    </div>
                  </td>
                  <td><strong>{khoi.ma_khoi}</strong></td>
                  <td>{khoi.ten_khoi}</td>
                  <td>{khoi.mo_ta || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(khoi)}
                        title={t('common.edit')}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(khoi)}
                        title={t('common.delete')}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal thêm/sửa */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedKhoi(null);
          setFormData({ maKhoi: '', tenKhoi: '' });
        }}
        title={selectedKhoi ? t('khoi.modalEditTitle') : t('khoi.modalAddTitle')}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="maKhoi">{t('khoi.fieldCode')}</label>
            <input
              type="text"
              className="form-control"
              id="maKhoi"
              value={formData.maKhoi}
              onChange={(e) => setFormData({ ...formData, maKhoi: e.target.value })}
              required
              placeholder={t('khoi.fieldCodePlaceholder')}
            />
          </div>
          <div className="form-group">
            <label htmlFor="tenKhoi">{t('khoi.fieldName')}</label>
            <input
              type="text"
              className="form-control"
              id="tenKhoi"
              value={formData.tenKhoi}
              onChange={(e) => setFormData({ ...formData, tenKhoi: e.target.value })}
              required
              placeholder={t('khoi.fieldNamePlaceholder')}
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary">
              {selectedKhoi ? t('common.update') : t('common.add')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Confirm xóa */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelectedKhoi(null);
        }}
        onConfirm={confirmDelete}
        title={t('khoi.confirmDeleteTitle')}
        message={t('khoi.confirmDeleteMsg', { name: selectedKhoi?.ten_khoi })}
      />

      {/* Import modal */}
      <ImportExcel
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
        title={t('khoi.importTitle')}
        templateColumns={['Mã khối', 'Tên khối', 'Mô tả']}
        templateData={[
          ['11', 'Khối 11', 'Khối lớp 11'],
          ['12', 'Khối 12', 'Khối lớp 12']
        ]}
        templateFileName="khoi_template.xlsx"
      />
    </div>
  );
};

export default KhoiPage;
